<template>
  <div class="wrapper w-100">
    <survey-steps
      v-model="pageIndex"
      :pages="innerValue"
      :formData="[]"
      class="px-md-12 py-md-9 px-6 py-4"
    ></survey-steps>
    <div class="clickLayer text-right rounded" @click.stop="edit">
      <div
        class="mt-3 mr-3 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="edit"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit Pages</span>
        </v-tooltip>
      </div>
    </div>

    <v-dialog
      v-model="openEditor"
      scrollable
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <ValidationObserver ref="observer" tag="form" @submit.prevent="onSubmit">
        <v-card rounded="0">
          <div class="elevation-3 primary ">
            <v-container class="pa-0">
              <dialog-title
                v-model="openEditor"
                title="Edit Pages"
                class="white--text pl-md-0"
              >
                <template v-slot:actions="{ close }">
                  <v-btn dark icon @click="close">
                    <v-icon>$close</v-icon></v-btn
                  >
                </template>
              </dialog-title>
            </v-container>
          </div>
          <v-card-text ref="body">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <div class="d-flex align-end mb-3">
                    <p class="flex-fill">
                      Use the table below to add, edit, or remove a page.
                    </p>
                    <v-tooltip left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          fab
                          color="primary"
                          small
                          elevation="0"
                          @click="addNewPage"
                          :disabled="addingPage || locked"
                          ><font-awesome-icon icon="plus"></font-awesome-icon
                        ></v-btn>
                      </template>
                      <span>Add Page</span>
                    </v-tooltip>
                  </div>
                  <table class="stripped">
                    <tr>
                      <td class="py-2 text-center2 grey darken-3 white--text">
                        Active
                      </td>
                      <td
                        class="py-2 w-100 text-left grey darken-3 white--text"
                      >
                        Title
                      </td>
                      <td
                        class="py-2 text-left text-left grey darken-3 white--text"
                      >
                        Key
                      </td>
                      <td
                        class="py-2 text-center text-left grey darken-3 white--text"
                      >
                        Actions
                      </td>
                    </tr>
                    <template v-for="(page, index) in innerValue">
                      <tr
                        :key="`read_${index}`"
                        v-if="editingPage !== index && deletingPage !== index"
                        @click="activePage = index"
                        class="pointer hover"
                      >
                        <td class="py-3 px-4">
                          <v-btn
                            icon
                            :color="activePage === index ? 'secondary' : null"
                            @click.stop="activePage = index"
                          >
                            <v-icon>
                              {{
                                activePage === index ? "$checked" : "$unchecked"
                              }}</v-icon
                            >
                          </v-btn>
                        </td>
                        <td class="py-3 px-4">
                          {{ page.displayName }}
                        </td>
                        <td class="py-3 px-4">
                          {{ page.key }}
                        </td>
                        <td class="text-no-wrap py-3 px-4">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                @click="editRow(page, index)"
                                :disabled="locked"
                              >
                                <v-icon small>$edit</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                @click="deletePage(page, index)"
                                :disabled="locked"
                              >
                                <v-icon small>$delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                :disabled="index === 0 || locked"
                                @click="move(index, -1)"
                              >
                                <v-icon small>$sortUp</v-icon>
                              </v-btn>
                            </template>
                            <span>Move Up</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                :disabled="
                                  index + 1 == innerValue.length || locked
                                "
                                @click="move(index, 1)"
                              >
                                <v-icon small>$sortDown</v-icon>
                              </v-btn>
                            </template>
                            <span>Move Down</span>
                          </v-tooltip>
                        </td>
                      </tr>
                      <ValidationObserver
                        v-slot="{ invalid }"
                        :key="`edit_${index}`"
                        v-if="editingPage === index && !locked"
                        tag="tr"
                      >
                        <td class="px-4 py-4"></td>
                        <td class="px-4 py-4" style="vertical-align: top">
                          <ValidationProvider
                            rules="required|min:2|max:2000"
                            v-slot="{ errors }"
                            vid="pageDisplayName"
                          >
                            <v-text-field
                              label="Text*"
                              v-model="editPage.displayName"
                              outlined
                              :error-messages="errors"
                              :hide-details="errors.length == 0"
                              autofocus
                              @keydown.enter.prevent="confirmEditRow"
                            ></v-text-field>
                          </ValidationProvider>
                        </td>
                        <td class="px-4 py-4" style="vertical-align: top">
                          <ValidationProvider
                            :rules="{
                              required: true,
                              max: 100,
                              notTaken: pageKeys.filter(x => x !== page.key)
                            }"
                            v-slot="{ errors }"
                            vid="pageKey"
                          >
                            <v-text-field
                              label="Key*"
                              v-model="editPage.key"
                              outlined
                              :error-messages="errors"
                              :hide-details="errors.length == 0"
                              style="width: 150px"
                              @keydown.enter.prevent="confirmEditRow"
                            ></v-text-field>
                          </ValidationProvider>
                        </td>
                        <td class="text-no-wrap px-4 py-4 text-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                outlined
                                @click="confirmEditRow"
                                color="green darken-2"
                                :disabled="invalid"
                                class="mr-2"
                                large
                              >
                                <v-icon small>$check</v-icon>
                              </v-btn>
                            </template>
                            <span>Confirm</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                color="red darken-2"
                                v-bind="attrs"
                                v-on="on"
                                icon
                                outlined
                                large
                                @click="cancelEditRow"
                              >
                                <v-icon small>$close</v-icon>
                              </v-btn>
                            </template>
                            <span>Cancel</span>
                          </v-tooltip>
                        </td>
                      </ValidationObserver>
                      <tr
                        :key="`delete_${index}`"
                        v-if="deletingPage === index && !locked"
                      >
                        <td colspan="4" class="red lighten-4 red--text py-3">
                          <div class="d-flex align-center">
                            <div class="flex-fill">
                              You are about to delete
                              <strong>{{ page.key }}</strong
                              >, and all of its questions. Are you sure?
                            </div>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="confirmDelete"
                                  color="red darken-2 white--text"
                                  class="mr-2"
                                >
                                  Yes, Delete
                                </v-btn>
                              </template>
                              <span>Confirm</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="cancelDelete"
                                >
                                  Cancel
                                </v-btn>
                              </template>
                              <span>Cancel</span>
                            </v-tooltip>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <ValidationObserver
                      v-if="addingPage && !locked"
                      v-slot="{ invalid }"
                      tag="tr"
                    >
                      <td class="py-3 px-4">
                        <v-btn disabled icon>
                          <v-icon> {{ "$unchecked" }}</v-icon>
                        </v-btn>
                      </td>
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          rules="required|min:2|max:2000"
                          v-slot="{ errors }"
                          vid="addPageDisplayName"
                        >
                          <v-text-field
                            label="Text*"
                            v-model="addPage.displayName"
                            outlined
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            autofocus
                            @keydown.enter.prevent="confirmAdd"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          :rules="{
                            required: true,
                            max: 100,
                            notTaken: pageKeys
                          }"
                          v-slot="{ errors }"
                          vid="addPageKey"
                        >
                          <v-text-field
                            label="Key*"
                            v-model="addPage.key"
                            outlined
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            style="width: 150px"
                            @keydown.enter.prevent="confirmAdd"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="text-no-wrap px-4 py-4 text-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              outlined
                              @click="confirmAdd"
                              color="green darken-2"
                              :disabled="invalid"
                              class="mr-2"
                              large
                            >
                              <v-icon small>$check</v-icon>
                            </v-btn>
                          </template>
                          <span>Confirm</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              color="red darken-2"
                              v-bind="attrs"
                              v-on="on"
                              icon
                              outlined
                              large
                              @click="cancelAdd"
                            >
                              <v-icon small>$close</v-icon>
                            </v-btn>
                          </template>
                          <span>Cancel</span>
                        </v-tooltip>
                      </td>
                    </ValidationObserver>
                  </table>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="px-4 py-0 elevation-3">
            <v-container class="d-flex align-center">
              <small class="flex-fill">*indicates required field</small>
              <v-btn text color="primary" @click="openEditor = false">
                Cancel
              </v-btn>
              <v-btn v-if="!locked" type="submit" color="primary">
                Ok
              </v-btn>
            </v-container>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  position: relative;
  z-index: 0;

  .clickLayer {
    opacity: 0;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.15);
    transition: opacity 300ms ease-in-out;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }
}
</style>

<script>
import Vue from "vue";
import DialogTitle from "./DialogTitle.vue";
import SurveySteps from "./Survey.Steps.vue";
import equal from "deep-is";

const newPage = () => ({
  displayName: "",
  key: "",
  questions: [],
  rules: []
});

export default {
  components: { DialogTitle, SurveySteps },
  data: () => ({
    innerValue: [],
    openEditor: false,
    addingPage: false,
    addPage: newPage(),
    editingPage: -1,
    editPage: newPage(),
    deletingPage: -1,
    activePage: -1
  }),
  props: {
    pageIndex: {
      type: Number
    },
    value: {
      type: Array
    },
    locked: {
      type: Boolean
    }
  },
  computed: {
    pageKeys() {
      return this.innerValue.map(x => x.key);
    }
  },
  watch: {
    value(val) {
      if (!equal(val, this.innerValue)) this.innerValue = val;
    },
    openEditor(val) {
      if (val) {
        this.innerValue = JSON.parse(JSON.stringify(this.value));
        this.activePage = JSON.parse(JSON.stringify(this.pageIndex));

        this.addingPage = false;
        this.addPage = newPage();
        this.editingPage = -1;
        this.editPage = newPage();
        this.deletingPage = -1;

        if (this.$refs.body) {
          setTimeout(() => (this.$refs.body.scrollTop = 0), 1);
        }
      }
    }
  },
  methods: {
    edit() {
      this.openEditor = true;
    },
    onSubmit() {
      this.cancelAdd();

      this.$nextTick(() => {
        this.$refs.observer.validate().then(isValid => {
          if (isValid) {
            if (this.activePage < this.innerValue.length)
              this.$emit("change-page", this.activePage);
            else if (this.innerValue.length < this.pageIndex + 1) {
              this.$emit("change-page", this.innerValue.length - 1);
            } else {
              let testIndex = this.innerValue.findIndex(
                x => x.key === this.value[this.pageIndex].key
              );

              if (testIndex != -1 && testIndex != this.pageIndex) {
                this.$emit("change-page", testIndex);
              }
            }

            this.$emit("input", this.innerValue);

            this.openEditor = false;
          }
        });
      });
    },
    editRow(page, index) {
      this.editPage = JSON.parse(JSON.stringify(page));
      this.editingPage = index;
    },
    cancelEditRow() {
      this.editPage = newPage();
      this.editingPage = -1;
    },
    confirmEditRow() {
      Vue.set(this.innerValue, this.editingPage, this.editPage);
      this.cancelEditRow();
    },
    addNewPage() {
      this.addPage = newPage();
      this.addingPage = true;
    },
    cancelAdd() {
      this.addPage = newPage();
      this.addingPage = false;
    },
    confirmAdd() {
      this.innerValue.push(this.addPage);
      this.cancelAdd();
      this.activePage = this.innerValue.length - 1;
    },
    deletePage(page, index) {
      this.deletingPage = index;
    },
    cancelDelete() {
      this.deletingPage = -1;
    },
    confirmDelete() {
      this.innerValue.splice(this.deletingPage, 1);
      this.cancelDelete();
    },
    move(index, increment) {
      let mirror = JSON.parse(JSON.stringify(this.innerValue));
      let old = mirror[index + increment];

      mirror[index + increment] = mirror[index];
      mirror[index] = old;

      if (index === this.activePage) this.activePage = index + increment;

      this.innerValue = mirror;
    }
  },
  mounted() {
    if (this.value) this.innerValue = this.value;
  }
};
</script>
